// MUI
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

/**
 * Dialog for handling over-entitlement scenarios in Draft Orders.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.setOpen - Function to set dialog open state
 * @param {string} props.reason - Current reason text
 * @param {function} props.setReason - Function to set reason text
 * @param {function} props.onConfirm - Callback when user confirms (Warning/Escalate modes)
 * @param {"Warning"|"Escalate"|"Block"} props.mode - Dialog mode determining behavior
 */
export const DraftEntitlementDialog = ({
  open,
  setOpen,
  reason,
  setReason,
  onConfirm,
  mode,
}) => {
  const handleClose = () => {
    setOpen(false);
    setReason("");
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(reason);
    }
    setReason("");
    setOpen(false);
  };

  const isBlockMode = mode === "Block";

  const getDialogTitle = () => {
    switch (mode) {
      case "Block":
        return "Cannot Add Item";
      case "Escalate":
        return "Item Over Entitlement - Approval Required";
      case "Warning":
      default:
        return "Item Over Entitlement";
    }
  };

  const getDialogContent = () => {
    switch (mode) {
      case "Block":
        return "You cannot add this item as it would exceed the entitlement allowance. Please contact your manager if you believe this is an error.";
      case "Escalate":
        return "This item exceeds the entitlement. Please enter a reason below. This order will require manager approval before it can be processed.";
      case "Warning":
      default:
        return "This item is over entitlement. Please enter a reason to continue. Note: This order can still be checked out immediately.";
    }
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={handleClose}
      aria-labelledby="draft-entitlement-dialog"
    >
      <DialogTitle id="draft-entitlement-dialog-title">
        {getDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{getDialogContent()}</DialogContentText>

        {!isBlockMode && (
          <TextField
            sx={{ mt: 2 }}
            id="reason-input"
            fullWidth
            label="Reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            slotProps={{
              htmlInput: { maxLength: 99 },
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {isBlockMode ? (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="outlined" color="error" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={reason === ""}
            >
              Continue
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
